import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { Divider, Button, notification, Modal, Space } from 'antd';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  Area,
  ListAreasQuery,
} from '@/core/domain/operational/Areas/Areas.types';
import {
  useAreas,
  useDeleteArea,
} from '@/core/domain/operational/Areas/Areas.hooks';

import { PageProps } from '@/types/PageProps';

import TabContentWrapper from '@/components/_common/TabContentWrapper';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import AreaReportModal from '@/components/Operational/Areas/AreaReportModal';
import { getTableScrollX } from '@/utils/table';
import { AppPageHeader } from '@/components/_common';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import AreasExtraFilters from '@/components/Operational/Areas/AreaExtraFilters';
import { useDebounce, useDisclosure } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import AreaCreateModal from '@/components/Operational/Areas/AreaCreateModal';
import { hasPermissions } from '@/utils/functions/hasPermissions';

function AreasPage({ route }: PageProps) {
  const intl = useIntl();
  const [modalGeneralVisible, setModalGeneralVisible] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [modalCreateAreaOpen, modalCreateAreaActions] = useDisclosure();
  const [areaSelected, setAreaSelected] = useState();

  const [queryParams, setQueryParams] = useState<ListAreasQuery>({
    order: 'ASC',
    active: true,
    ...convertParams<ListAreasQuery>({
      name: 'string',
      active: 'boolean',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    refetch: refetchAreas,
  } = useAreas(queryDebounce);

  usePersistSearchParams(queryDebounce);

  const areas = flatInfiniteData(data);

  const deleteMutation = useDeleteArea();

  useEffect(() => {
    if (areaSelected) {
      toggleModalGeneral();
    }
  }, [areaSelected]);

  function toggleModalGeneral() {
    if (modalGeneralVisible) {
      setAreaSelected(undefined);
    }

    setModalGeneralVisible(!modalGeneralVisible);
  }

  function toggleReportModal() {
    setReportModalVisible(!reportModalVisible);
  }

  function handleRemoveClick(area: Area) {
    Modal.confirm({
      title: intl.formatMessage({ id: 'areas.modal.remove.title' }),
      content: `${intl.formatMessage({
        id: 'areas.modal.remove.content',
      })} "${area.name}"`,
      onOk: async () => {
        // Fetch Service to remove area item.
        await deleteMutation.mutateAsync(area.id);

        await refetchAreas();

        notification.success({
          message: intl.formatMessage({
            id: 'areas.remove.notification.success',
          }),
          description: intl.formatMessage({
            id: 'areas.remove.notification.successDescription',
          }),
        });
      },
      okText: intl.formatMessage({
        id: 'areas.modal.remove.button.ok',
      }),
      okType: 'danger',
    });
  }

  const columns: ColumnsType<Area> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: intl.formatMessage({ id: 'areas.general.form.description' }),
      ellipsis: true,
      width: 300,
    },
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (area) => (
        <Space>
          <EditIcon
            redirect={`/app/operational/areas/${area.id}`}
            disabled={!hasPermissions([Authorities.REGISTER_AREA_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(area)}
            disabled={!hasPermissions([Authorities.REGISTER_AREA_DELETE])}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Helmet
        title={intl.formatMessage({ id: 'layout.menu.operational.areas' })}
      />

      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={() => toggleReportModal()}
              disabled={!hasPermissions([Authorities.REGISTER_AREA_REPORTS])}
            >
              {intl.formatMessage({
                id: 'commons.page.button.generateReport',
              })}
            </Button>

            <Button
              block
              type="primary"
              onClick={modalCreateAreaActions.open}
              disabled={!hasPermissions([Authorities.REGISTER_AREA_ADD])}
            >
              {intl.formatMessage({
                id: 'areas.page.button.newService',
              })}
            </Button>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          searchValue={queryParams.name}
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, name: value }))
          }
          extraFilters={
            <AreasExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />
        <InfiniteTable<Area>
          rowKey={(row) => row.id}
          loading={isFetching}
          dataSource={areas}
          columns={columns}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <AreaReportModal
          visible={reportModalVisible}
          handleVisible={() => toggleReportModal()}
        />

        <AreaCreateModal
          onCancel={modalCreateAreaActions.close}
          open={modalCreateAreaOpen}
        />
      </TabContentWrapper>
    </div>
  );
}

AreasPage.propTypes = {};

export default AreasPage;
